import { useNavigationPresentation, navigationBreadcrumb } from "@/lib/tradeNavigation";
// Top navigation pieces: Chat shortcut, Help menu, Profile/Company menu and
// the route-derived breadcrumb bar. All links reuse existing routes/permissions.
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  MessageSquare, HelpCircle, ChevronDown, ChevronRight, User, Palette, CreditCard,
  Link2, LifeBuoy, Sparkles, LogOut, Moon, Sun, ShieldCheck, BookOpen,
} from "lucide-react";
import api from "@/lib/api";
import { useTheme } from "@/lib/theme";

export function ChatButton({ unread }) {
  return (
    <Link
      to="/app/chat"
      aria-label={unread > 0 ? `Chat — ${unread} unread` : "Chat"}
      data-testid="topbar-chat"
      className="relative w-10 h-10 rounded-lg flex items-center justify-center text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-[#151C27] transition-colors duration-150"
    >
      <MessageSquare className="w-[18px] h-[18px]" strokeWidth={1.9} />
      {unread > 0 && (
        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-[#FF5F15] text-white text-[11px] font-bold leading-none flex items-center justify-center" data-testid="nav-chat-unread">
          {unread > 99 ? "99+" : unread}
        </span>
      )}
    </Link>
  );
}

// Red "What's new" pill shown in the top bar until the user opens the What's New page.
export function WhatsNewBadge({ count }) {
  const nav = useNavigate();
  if (!count) return null;
  return (
    <button type="button" onClick={() => nav("/app/changelog")} data-testid="whats-new-badge"
      aria-label={`${count} new update${count === 1 ? "" : "s"} — open What's New`}
      className="fl-whats-new-badge relative hidden sm:inline-flex items-center justify-center gap-1.5 h-8 min-w-[32px] px-2 sm:h-9 sm:pl-2.5 sm:pr-3 rounded-full bg-red-600 hover:bg-red-700 text-white text-xs font-bold shadow-[0_4px_14px_rgba(220,38,38,.35)] transition-colors">
      <span className="relative hidden sm:flex w-2 h-2" aria-hidden="true">
        <span className="absolute inline-flex w-full h-full rounded-full opacity-75 motion-safe:animate-ping" style={{ background: "#fff" }} />
        <span className="relative inline-flex w-2 h-2 rounded-full" style={{ background: "#fff" }} />
      </span>
      <span className="hidden sm:inline">What's new</span>
      <span className="hidden sm:flex min-w-[18px] h-[18px] px-1 rounded-full text-[10px] items-center justify-center" style={{ background: "#fff", color: "#dc2626" }}>{count > 99 ? "99+" : count}</span>
      <span className="sm:hidden text-[11px]">{count > 99 ? "99+" : count}</span>
    </button>
  );
}

export function HelpMenu({ role, hasUnseenChange, unseenCount = 0 }) {
  const nav = useNavigate();
  const canHelpCentre = ["super_admin", "admin", "office", "quantity_surveyor"].includes(role);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="Help"
          data-testid="topbar-help"
          className="relative w-10 h-10 rounded-lg flex items-center justify-center text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-white hover:bg-zinc-100 dark:hover:bg-[#151C27] transition-colors duration-150"
        >
          <HelpCircle className="w-[18px] h-[18px]" strokeWidth={1.9} />
          {hasUnseenChange && <span className="sm:hidden absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center ring-2 ring-white dark:ring-[#0B0F15]" style={{ background: "#dc2626", color: "#fff" }} data-testid="help-unseen-dot" aria-hidden="true">{unseenCount > 99 ? "99+" : unseenCount || ""}</span>}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={10} className="w-60 rounded-lg dark:bg-[#101620] dark:border-[#1F2937]" data-testid="help-menu">
        {canHelpCentre && (
          <DropdownMenuItem onClick={() => nav("/app/help")} data-testid="help-menu-centre">
            <BookOpen className="w-4 h-4 mr-2 text-[#FF5F15]" /> Help Centre
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => nav("/app/support")} data-testid="help-menu-support">
          <LifeBuoy className="w-4 h-4 mr-2 text-[#FF5F15]" /> Contact Support
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => nav("/app/changelog")} data-testid="help-menu-whats-new">
          <Sparkles className="w-4 h-4 mr-2 text-[#FF5F15]" /> What's New
          {hasUnseenChange && <span className="ml-auto min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center" style={{ background: "#dc2626", color: "#fff" }} data-testid="help-menu-whats-new-count">{unseenCount > 99 ? "99+" : unseenCount || "New"}</span>}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="px-2 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400 dark:text-zinc-500">Keyboard Shortcuts</div>
        <div className="px-2 pb-2 space-y-1 text-[11.5px] text-zinc-500 dark:text-zinc-400">
          <div className="flex justify-between"><span>Global search</span><kbd className="font-mono font-bold text-zinc-700 dark:text-zinc-300">⌘K / Ctrl+K</kbd></div>
          <div className="flex justify-between"><span>Quick search</span><kbd className="font-mono font-bold text-zinc-700 dark:text-zinc-300">/</kbd></div>
          <div className="flex justify-between"><span>Close dialogs</span><kbd className="font-mono font-bold text-zinc-700 dark:text-zinc-300">Esc</kbd></div>
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const ROLE_LABEL = (r = "") => r.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export function ProfileMenu({ user, onLogout, showRole = false }) {
  const nav = useNavigate();
  const { setTheme, resolved } = useTheme();
  const isDark = resolved === "dark";
  const role = user.role;
  const isAdmin = ["super_admin", "admin"].includes(role);
  const isOffice = ["super_admin", "admin", "office"].includes(role);
  const [kiriHidden, setKiriHidden] = useState(() => {
    try { return localStorage.getItem(`fl_kiri_hidden_${user.id}`) === "1"; } catch { return false; }
  });
  useEffect(() => {
    const onHidden = () => setKiriHidden(true);
    const onShow = () => setKiriHidden(false);
    window.addEventListener("fl:kiri-hidden", onHidden);
    window.addEventListener("fl:kiri-show", onShow);
    return () => {
      window.removeEventListener("fl:kiri-hidden", onHidden);
      window.removeEventListener("fl:kiri-show", onShow);
    };
  }, []);
  const avatarSrc = user.avatar_path
    ? `${api.defaults.baseURL}/files/view?path=${encodeURIComponent(user.avatar_path)}&auth=${encodeURIComponent(localStorage.getItem("fl_token") || "")}`
    : null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="profile-menu-trigger" aria-label="Account menu"
          className="flex items-center gap-2.5 pl-1 pr-1.5 py-1 rounded-lg hover:bg-zinc-100 dark:hover:bg-[#151C27] transition-colors duration-150">
          <Avatar className="w-8 h-8 rounded-full">
            {avatarSrc ? <AvatarImage src={avatarSrc} alt={user.name} /> : null}
            <AvatarFallback className="bg-[#FF5F15] text-white text-[11px] font-bold rounded-full">
              {user.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          <span className="hidden xl:block leading-tight text-left">
            <span className="block text-[12.5px] font-bold">{user.name}</span>
            <span className="block text-[10.5px] text-zinc-500 dark:text-[#8D98A8] truncate max-w-[130px]">{showRole ? ROLE_LABEL(role) : (user.org?.name || (role || "").replace(/_/g, " "))}</span>
          </span>
          <ChevronDown className="hidden xl:block w-3.5 h-3.5 text-zinc-400" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={10} className="w-60 rounded-lg dark:bg-[#101620] dark:border-[#1F2937]" data-testid="profile-menu">
        <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400 dark:text-zinc-500">My Account</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => nav("/app/profile")} data-testid="profile-menu-profile">
          <User className="w-4 h-4 mr-2 text-[#FF5F15]" /> My Profile
        </DropdownMenuItem>
        {isOffice && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-[11px] font-bold uppercase tracking-[0.14em] text-zinc-400 dark:text-zinc-500">Company</DropdownMenuLabel>
            <DropdownMenuItem onClick={() => nav("/app/branding")} data-testid="profile-menu-branding">
              <Palette className="w-4 h-4 mr-2 text-[#FF5F15]" /> Company Settings
            </DropdownMenuItem>
            {isAdmin && (
              <DropdownMenuItem onClick={() => nav("/app/billing")} data-testid="profile-menu-billing">
                <CreditCard className="w-4 h-4 mr-2 text-[#FF5F15]" /> Billing
              </DropdownMenuItem>
            )}
            {isAdmin && (
              <DropdownMenuItem onClick={() => nav("/app/integrations/xero")} data-testid="profile-menu-xero">
                <Link2 className="w-4 h-4 mr-2 text-[#FF5F15]" /> Xero Integration
              </DropdownMenuItem>
            )}
          </>
        )}
        {role === "super_admin" && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => nav("/app/platform")} data-testid="profile-menu-platform">
              <ShieldCheck className="w-4 h-4 mr-2 text-[#FF5F15]" /> Platform Administration
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={(e) => { e.preventDefault(); setTheme(isDark ? "light" : "dark"); }} data-testid="profile-menu-theme">
          {isDark ? <Moon className="w-4 h-4 mr-2 text-[#FF5F15]" /> : <Sun className="w-4 h-4 mr-2 text-[#FF5F15]" />}
          <span className="flex-1">Dark Mode</span>
          <span className={`w-8 h-[18px] rounded-full relative transition-colors ${isDark ? "bg-[#FF5F15]" : "bg-zinc-300"}`}>
            <span className={`absolute top-[2px] w-[14px] h-[14px] rounded-full bg-white transition-all ${isDark ? "left-[16px]" : "left-[2px]"}`} />
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {kiriHidden && (
          <DropdownMenuItem onClick={() => window.dispatchEvent(new CustomEvent("fl:kiri-show"))} data-testid="profile-menu-show-kiri">
            <Sparkles className="w-4 h-4 mr-2 text-[#FF5F15]" /> Show Kiri assistant
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={onLogout} data-testid="profile-menu-logout" className="text-red-500 focus:text-red-500">
          <LogOut className="w-4 h-4 mr-2" /> Sign Out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// Slim breadcrumb bar under the top nav. `leading` (e.g. the Back pill) shares
// the same row so office pages get one compact context bar instead of two strips.
export function Breadcrumbs({ leading = null, hideTrail = false }) {
  const loc = useLocation();
  const { user, trades } = useNavigationPresentation();
  const trail = hideTrail ? [] : navigationBreadcrumb(loc.pathname, user, trades) || [];
  if (!trail.length && !leading) return null;
  if (leading) return (
    <nav aria-label="Breadcrumb" data-testid="breadcrumbs" className="fl-contextbar">
      {leading}
      {trail.length > 0 && <span className="fl-contextbar-sep" aria-hidden="true" />}
      <span className="fl-contextbar-trail">
        {trail.map((seg, i) => (
          <span key={i} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="w-3.5 h-3.5 opacity-50" aria-hidden="true" />}
            <span className={i === trail.length - 1 ? "is-current" : ""} aria-current={i === trail.length - 1 ? "page" : undefined}>{seg}</span>
          </span>
        ))}
      </span>
    </nav>
  );
  return (
    <nav
      aria-label="Breadcrumb"
      data-testid="breadcrumbs"
      className="hidden lg:flex items-center gap-1.5 px-6 py-1.5 border-b border-zinc-200 dark:border-white/[0.06] bg-zinc-50/80 dark:bg-[#0B1018]/80 backdrop-blur text-[11px] text-zinc-500 dark:text-zinc-500"
    >
      {trail.map((seg, i) => {
        const last = i === trail.length - 1;
        return (
          <span key={i} className="flex items-center gap-1.5">
            {i > 0 && <ChevronRight className="w-3 h-3 text-zinc-300 dark:text-zinc-700" />}
            <span className={last ? "font-bold text-zinc-800 dark:text-zinc-200" : "font-medium"}>{seg}</span>
          </span>
        );
      })}
    </nav>
  );
}
